// Copied verbatim from ptc-core CRM (spec 2026-07-22). Pure operator table + logic helpers plus a
// JSX value-input renderer shared by ColumnHeaderFilter — not itself a component.
import { Input, Dropdown, Field, Option } from '@fluentui/react-components';
import { EntityColumn } from './types';
import i18n from '../../i18n';
import { CalendarDateInput } from '../dates/CalendarDateInput';

// CRM-UX-01 — shared Dataverse-style filter-operator logic (spec §3.3), extracted from
// ColumnFilters so the standalone filter bar AND the per-column header filter popover
// (ColumnHeaderFilter) share ONE operator/value-input implementation. Never duplicate this.
//
// Fluent v9 gotcha: controlled Dropdowns are wrapped in <Field> for their accessible name —
// NEVER aria-label on them (known render-loop hang that also wedges vitest).

export type FilterType = NonNullable<EntityColumn<unknown>['filterType']>;

export interface OperatorDef {
  op: string;
  label: string;
}

/** An operator whose label is read in the UI language each time it is shown. */
function op(value: string, key: string): OperatorDef {
  return {
    op: value,
    get label() {
      return i18n.t(`filterOps.${key}`);
    },
  };
}

/**
 * "Has no value" inside a lookup column's `in`/`notIn` value list (fleet filter contract v3 §16.8 / C86). THE FLEET'S
 * value, identical to ptc-core's `web/src/components/columnFilterOps.tsx` @ 7d1f5f9 and tax-lifecycle's, so a URL or a
 * saved view means the same thing in every app. `picklistSelection.ts` (copied from core) reads and writes it.
 */
export const NO_VALUE_TOKEN = '__TL_NO_VALUE__';

/** A boolean column's lookup options (C86: a boolean is a lookup with exactly these two), in the UI language. */
export function booleanLookupOptions(): { value: string; label: string }[] {
  return [
    { value: 'true', label: i18n.t('filters.lookup.yes') },
    { value: 'false', label: i18n.t('filters.lookup.no') },
  ];
}

/** Ops that take no value: the key is emitted with an empty value and the input is disabled. */
export const DATA_OPS = new Set(['containsData', 'noData']);

/** Operator menus per filterType, default first (spec §3.3). */
export const OPERATORS: Record<FilterType, OperatorDef[]> = {
  text: [
    op('contains', 'contains'),
    op('notContains', 'notContains'),
    op('startsWith', 'startsWith'),
    op('endsWith', 'endsWith'),
    op('eq', 'equals'),
    op('ne', 'doesNotEqual'),
    op('containsData', 'containsData'),
    op('noData', 'noData'),
  ],
  // Fleet filter contract v2 §12.1 RULE D1 — Dataverse's date menu, verbatim and in order. "On" and
  // "On or before" are whole-day (Q3); BL-142 landed in this engine (#171), so RULE D3 permits them.
  // "This fiscal period" is ABSENT, not disabled: no fiscal calendar is configured here (D5, C54).
  // Presets are pseudo-ops that stage a gte/lt TOKEN pair (never resolved dates — C52).
  'date-range': [
    op('eq', 'on'),
    op('gte', 'onOrAfter'),
    op('lte', 'onOrBefore'),
    op('preset:today', 'today'),
    op('preset:yesterday', 'yesterday'),
    op('preset:tomorrow', 'tomorrow'),
    op('preset:thisWeek', 'thisWeek'),
    op('preset:thisMonth', 'thisMonth'),
    op('preset:thisYear', 'thisYear'),
    op('between', 'between'),
    op('containsData', 'hasData'),
    op('noData', 'isEmpty'),
  ],
  number: [
    op('eq', 'equals'),
    op('ne', 'notEqual'),
    op('between', 'between'),
    op('gt', 'greaterThan'),
    op('gte', 'greaterOrEqual'),
    op('lt', 'lessThan'),
    op('lte', 'lessOrEqual'),
    op('containsData', 'containsData'),
    op('noData', 'noData'),
  ],
  picklist: [
    op('in', 'anyOf'),
    op('notIn', 'noneOf'),
    op('containsData', 'containsData'),
    op('noData', 'noData'),
  ],
  // C86: a boolean column filters through the same lookup combobox, so it takes `in`/`notIn` too. `eq` stays first and
  // in the table so a legacy `filter.<col>.eq` is still read (and translated by picklistSelection), never dropped.
  boolean: [op('eq', 'is'), op('in', 'anyOf'), op('notIn', 'noneOf'), op('containsData', 'containsData'), op('noData', 'noData')],
};

/**
 * D1's presets: each is a half-open TOKEN pair on one column. The URL carries the tokens, and they
 * resolve when the list is read (C52) — a *Yesterday* saved on the 19th still means yesterday on the
 * 25th.
 */
export const DATE_PRESETS: Record<string, { gte: string; lt: string }> = {
  'preset:today': { gte: '{{today}}', lt: '{{tomorrow}}' },
  'preset:yesterday': { gte: '{{yesterday}}', lt: '{{today}}' },
  'preset:tomorrow': { gte: '{{tomorrow}}', lt: '{{dayAfterTomorrow}}' },
  'preset:thisWeek': { gte: '{{weekStart}}', lt: '{{weekEnd}}' },
  'preset:thisMonth': { gte: '{{monthStart}}', lt: '{{monthEnd}}' },
  'preset:thisYear': { gte: '{{yearStart}}', lt: '{{yearEnd}}' },
};

export function isPresetOp(op: string): boolean {
  return op in DATE_PRESETS;
}

/**
 * RULE D2 — the MENU is Dataverse's list; the WIRE set is a superset. These stay PARSEABLE so old
 * URLs, saved views and bookmarks keep narrowing, but they are not offered. The header shows one
 * only when the URL already carries it, so a stale link is readable rather than silently relabelled.
 */
export const PARSEABLE_ONLY_OPS: Partial<Record<FilterType, OperatorDef[]>> = {
  // A view or old link may carry a picklist `eq` (Reviews' status and Mine views do). Detecting it lets the C86 combobox
  // read it as a one-option selection (picklistSelection) instead of opening empty over an applied filter.
  picklist: [op('eq', 'is')],
  'date-range': [
    op('lt', 'before'),
    op('gt', 'after'),
  ],
};

/**
 * Whether a value may carry over to `newOp` on an operator switch. Date columns refuse to carry a
 * single date into Between (or a range into a single-date op), because the two date inputs would
 * render a half-filled pair the user never typed. Every other type keeps the original carry-always
 * behaviour unchanged.
 */
function carryFits<TItem>(col: EntityColumn<TItem>, newOp: string, carried: string): boolean {
  if (col.filterType !== 'date-range') return true;
  return (newOp === 'between') === carried.includes('..');
}

/** The preset whose exact token pair this column carries, or null. */
function detectPreset(colKey: string, value: Record<string, string>): string | null {
  const prefix = filterPrefix(colKey);
  const keys = Object.keys(value).filter((k) => k.startsWith(prefix));
  if (keys.length !== 2) return null;
  for (const [op, pair] of Object.entries(DATE_PRESETS)) {
    if (value[`${prefix}gte`] === pair.gte && value[`${prefix}lt`] === pair.lt) return op;
  }
  return null;
}

/** `filter.<colKey>.` prefix for a column's keys in the flat filter record. */
export function filterPrefix(colKey: string): string {
  return `filter.${colKey}.`;
}

/**
 * Resolve the active operator for a column from the filter record: a present
 * `filter.<col>.<op>` key always wins; else the remembered UI choice (for a column with no
 * value yet); else the filterType's default (first in {@link OPERATORS}).
 */
export function currentOp<TItem>(
  col: EntityColumn<TItem>,
  value: Record<string, string>,
  rememberedChoice?: string
): string {
  const ops = OPERATORS[col.filterType!];
  const prefix = filterPrefix(col.key);
  if (col.filterType === 'date-range') {
    const preset = detectPreset(col.key, value);
    if (preset) return preset;
  }
  for (const o of [...ops, ...(PARSEABLE_ONLY_OPS[col.filterType!] ?? [])]) {
    if (!isPresetOp(o.op) && `${prefix}${o.op}` in value) return o.op;
  }
  if (rememberedChoice && ops.some((o) => o.op === rememberedChoice)) return rememberedChoice;
  return ops[0].op;
}

/** Delete every `filter.<col>.*` key from `next` (mutates) — one condition per column. */
export function clearColumnFilters(next: Record<string, string>, colKey: string): void {
  const prefix = filterPrefix(colKey);
  for (const k of Object.keys(next)) {
    if (k.startsWith(prefix)) delete next[k];
  }
}

/**
 * Compute the next filter record after switching a column's operator, and whether the
 * caller should emit it. Carries the value over to the new key (data ops carry nothing);
 * only emits when there was a prior key for this column OR the new op is a data op
 * (matches the original ColumnFilters `selectOp` semantics exactly).
 */
export function applyOperatorChange<TItem>(
  col: EntityColumn<TItem>,
  oldOp: string,
  newOp: string,
  value: Record<string, string>
): { next: Record<string, string>; shouldEmit: boolean } {
  const prefix = filterPrefix(col.key);
  const hadKey = Object.keys(value).some((k) => k.startsWith(prefix));
  // A preset's value is a token pair, never a date to carry; and a date never becomes a preset.
  const carried = DATA_OPS.has(oldOp) || isPresetOp(oldOp) ? '' : value[`${prefix}${oldOp}`] ?? '';

  const next = { ...value };
  clearColumnFilters(next, col.key);
  const preset = DATE_PRESETS[newOp];
  if (preset) {
    next[`${prefix}gte`] = preset.gte;
    next[`${prefix}lt`] = preset.lt;
    return { next, shouldEmit: true };
  }
  if (DATA_OPS.has(newOp)) next[`${prefix}${newOp}`] = '';
  else if (carried !== '' && carryFits(col, newOp, carried)) next[`${prefix}${newOp}`] = carried;

  return { next, shouldEmit: hadKey || DATA_OPS.has(newOp) };
}

/** Compute the next filter record after setting a column's value under a given operator. */
export function applyFilterValue<TItem>(
  col: EntityColumn<TItem>,
  op: string,
  val: string,
  value: Record<string, string>
): Record<string, string> {
  const next = { ...value };
  clearColumnFilters(next, col.key);
  if (val !== '') next[`${filterPrefix(col.key)}${op}`] = val;
  return next;
}

export function renderValueInput<TItem>(
  col: EntityColumn<TItem>,
  op: string,
  value: string,
  onChange: (v: string) => void
) {
  const disabled = DATA_OPS.has(op);
  const shown = disabled ? '' : value;
  const label = { children: `${col.label} value`, className: 'sr-only' };

  switch (col.filterType) {
    case 'text':
      return (
        <Field label={label}>
          <Input size="small" disabled={disabled} value={shown} onChange={(_, d) => onChange(d.value)} />
        </Field>
      );
    case 'date-range': {
      // Presets carry no value: their token pair IS the condition.
      if (isPresetOp(op)) return null;
      // RULE D8 as amended in v2.3 (2026-10-27): the shared CalendarDateInput, NOT a native date input.
      // Chromium's own calendar writes the value when its month arrows are pressed; ours moves the view
      // only (C68). The value is always yyyy-MM-dd, typing a date stays possible (C55), and it renders
      // inline, so §6.6 I3 still has nothing portalled to register.
      if (op === 'between') {
        // Two inputs, never a range control emitting one param (§3). Neither can emit a {{token}} (C57).
        const [from = '', to = ''] = shown.split('..');
        const emit = (a: string, b: string) => onChange(a === '' && b === '' ? '' : `${a}..${b}`);
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Field label={{ children: `${col.label} from`, className: 'sr-only' }}>
              <CalendarDateInput size="small" disabled={disabled} value={from} max={to || undefined} onChange={(v) => emit(v, to)} />
            </Field>
            <Field label={{ children: `${col.label} to`, className: 'sr-only' }}>
              <CalendarDateInput size="small" disabled={disabled} value={to} min={from || undefined} onChange={(v) => emit(from, v)} />
            </Field>
          </div>
        );
      }
      return (
        <Field label={label}>
          <CalendarDateInput size="small" disabled={disabled} value={shown} onChange={onChange} />
        </Field>
      );
    }
    case 'number':
      return (
        <Field label={label}>
          <Input
            size="small"
            type={op === 'between' ? 'text' : 'number'}
            disabled={disabled}
            placeholder={op === 'between' ? '1..100' : undefined}
            value={shown}
            onChange={(_, d) => onChange(d.value)}
          />
        </Field>
      );
    case 'picklist': {
      const selected = shown ? shown.split(',') : [];
      const display = selected
        .map((s) => (col.filterOptions ?? []).find((o) => o.value === s)?.label ?? s)
        .join(', ');
      return (
        <Field label={label}>
          <Dropdown
            size="small"
            multiselect
            disabled={disabled}
            placeholder={i18n.t('filters.any')}
            value={display}
            selectedOptions={selected}
            onOptionSelect={(_, d) => onChange(d.selectedOptions.join(','))}
          >
            {(col.filterOptions ?? []).map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Dropdown>
        </Field>
      );
    }
    case 'boolean': {
      // CRM-UX-10 — explicit Yes/No (was a Switch: "toggle for filter — not obvious"). Single-select
      // Dropdown always fires onOptionSelect on click, even re-clicking the already-selected option
      // (see @fluentui/react-combobox useSelection.selectOption — no same-value guard), so re-selecting
      // the active choice is how this clears the filter, matching the Switch's old off-to-clear
      // behavior. No third "Any" Option: unset shows the "Any" placeholder like the picklist input.
      const boolValue = shown === 'true' || shown === 'false' ? shown : undefined;
      return (
        <Field label={label}>
          <Dropdown
            size="small"
            disabled={disabled}
            placeholder={i18n.t('filters.any')}
            value={boolValue === 'true' ? i18n.t('filters.yes') : boolValue === 'false' ? i18n.t('filters.no') : ''}
            selectedOptions={boolValue ? [boolValue] : []}
            onOptionSelect={(_, d) => {
              onChange(d.optionValue && d.optionValue !== boolValue ? d.optionValue : '');
            }}
          >
            <Option value="true">{i18n.t('filters.yes')}</Option>
            <Option value="false">{i18n.t('filters.no')}</Option>
          </Dropdown>
        </Field>
      );
    }
    default:
      return null;
  }
}
